import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { FilePlus2, Loader2 } from "lucide-react";
import { getSessionBoardSummary, newSessionFromSession } from "../../application/constellationSessions";

/**
 * On a past constellation session's detail page: offers to start a NEW session
 * seeded from that session's final board state (issue #197). Renders nothing
 * when the session has no constellation board with figures.
 */
export default function NewSessionFromBoard({ sessionId }: { sessionId: string }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [hasBoard, setHasBoard] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;
        getSessionBoardSummary(sessionId)
            .then((data) => {
                if (active) setHasBoard(data.has_board && data.puppet_count > 0);
            })
            .catch(() => {
                if (active) setHasBoard(false);
            });
        return () => {
            active = false;
        };
    }, [sessionId]);

    if (!hasBoard) return null;

    const start = async () => {
        setBusy(true);
        try {
            const data = await newSessionFromSession(sessionId);
            navigate(`/constellation/${data.board.id}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={start}
            disabled={busy}
            className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800 transition-colors hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-800/40 dark:bg-emerald-900/10 dark:text-emerald-300"
        >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />}
            {t("constellation.scenes.newSessionFromBoard")}
        </button>
    );
}
